// CRM B-I: „Vollständig anzeigen" — die formatierte Fassung einer erfassten Mail.
//
// Gespeichert wird nur Text (B-0 Punkt 1). Die HTML-Fassung wird **auf Klick** live aus
// dem Postfach geholt und **nicht** gespeichert. Drei Gründe stehen in G4: kein HTML in
// DynamoDB (das 400-KB-Limit wäre real statt theoretisch), keine veraltete Kopie, und es
// funktioniert auch für Kollegen — das Lambda liest mit der App-Berechtigung, nicht über
// die Outlook-Sitzung des Betrachters. Die Berechtigung prüft das Lambda
// (`mayTouchEmail`), nicht diese Komponente.
//
// ZWEI SCHICHTEN gegen fremdes HTML, und beide sind nötig:
//
//   1. DOMPurify (`sanitizeHtml` aus emailHtml.js) — dieselbe Grenze, die schon für
//      geteilte Signaturen gilt. Sie wirft `script`, `iframe`, `form` und alle
//      Event-Attribute weg, bevor irgendetwas ins DOM kommt.
//   2. Ein abgeschotteter iframe mit `sandbox=""` und einer CSP im `srcdoc`. Selbst wenn
//      Schicht 1 etwas durchließe, gibt es hier kein Skript, keinen gleichen Origin und
//      keinen Netzzugriff außer dem, den die CSP erlaubt.
//
// FERNBILDER SIND BLOCKIERT, bis der Nutzer sie nachlädt. Das ist keine Kosmetik: ein
// Zählpixel im HTML des Absenders verrät ihm beim Rendern, wann ein Mitarbeiter die Mail
// im Dashboard gelesen hat. Outlook blockiert externe Inhalte externer Absender aus genau
// diesem Grund. Eingebettete `data:`-Bilder sind davon nicht betroffen — sie verraten
// nichts.
import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { callLambda } from "../helpers/api/gateway";
import { client } from "../helpers/amplifyClient";
import EmailAttachments from "./EmailAttachments";
import { dateFormatter, dateTimeFormatter } from "./DateFormatter";
import { sanitizeHtml } from "./emailHtml";
import { getEmailForView } from "./graphql/activityFeed.queries";

/**
 * Sucht nach dem Trennpunkt zwischen der neuesten Nachricht und vorherigen Antworten
 * im E-Mail-Verlauf (Outlook-, Gmail-, Apple Mail- oder Plaintext-Zitate).
 */
export function findThreadBoundary(doc) {
  if (!doc || !doc.body) return null;

  // 1. Bekannte Wrapper-Elemente gängiger E-Mail-Clients
  const EXPLICIT_SELECTORS = [
    "#divRplyFwdMsg",
    "#x_divRplyFwdMsg",
    "#appendonsend",
    "#x_appendonsend",
    ".gmail_quote",
    "blockquote.gmail_quote",
    'blockquote[type="cite"]',
  ];

  for (const selector of EXPLICIT_SELECTORS) {
    const el = doc.querySelector(selector);
    if (el) return el;
  }

  // 2. Suche nach Trennlinien / Headern anhand von Inhalt
  const allElements = doc.body.querySelectorAll("*");
  const HR_SEPARATORS = /^[-_=\s]{5,}(Original Message|Urspr[üu]ngliche Nachricht|Forwarded Message|Weitergeleitete Nachricht)[-_=\s]*/i;
  const HEADER_VON_FROM = /^(Von|From|De)\s*:/i;
  const HEADER_SENT = /(Gesendet|Sent|Date|Datum|Envoyé)\s*:/i;
  const ON_WROTE = /^(On\s+.+?\s+wrote|Am\s+.+?\s+schrieb|Le\s+.+?\s+a écrit)\s*:/i;

  for (const el of allElements) {
    if (el.tagName === "HR") {
      const next = el.nextElementSibling;
      if (
        next &&
        (HEADER_VON_FROM.test(next.textContent?.trim() || "") ||
          HR_SEPARATORS.test(next.textContent?.trim() || ""))
      ) {
        return el;
      }
    }

    const text = el.textContent ? el.textContent.trim() : "";

    // "-----Ursprüngliche Nachricht-----" oder "________________________________"
    if (HR_SEPARATORS.test(text) || (/^_{15,}$/.test(text) && text.length >= 15)) {
      return el;
    }

    // "Am 01.09.2026 schrieb Max:" oder "On Mon, Sep 1, 2026 at 10:00 AM Max wrote:"
    if (ON_WROTE.test(text)) {
      return el;
    }

    // Outlook-Kopfzeilen: enthält Von/From und Gesendet/Sent
    if (
      (el.tagName === "P" || el.tagName === "DIV" || el.tagName === "TR" || el.tagName === "TABLE") &&
      HEADER_VON_FROM.test(text) &&
      HEADER_SENT.test(text)
    ) {
      const parent = el.parentElement;
      if (parent && parent !== doc.body && /border.*top/i.test(parent.getAttribute("style") || "")) {
        return parent;
      }
      return el;
    }

    // Outlook-Header mit <b>Von:</b> / <strong>From:</strong> etc.
    const bTags = el.querySelectorAll("b, strong");
    if (bTags.length >= 2) {
      const bTexts = Array.from(bTags).map((b) => b.textContent.trim());
      const hasFrom = bTexts.some((t) => HEADER_VON_FROM.test(t));
      const hasSentOrSubject = bTexts.some(
        (t) => HEADER_SENT.test(t) || /^(Betreff|Subject|Objet)\s*:/i.test(t)
      );
      if (hasFrom && hasSentOrSubject) {
        const parent = el.parentElement;
        if (parent && parent !== doc.body && /border.*top/i.test(parent.getAttribute("style") || "")) {
          return parent;
        }
        return el;
      }
    }
  }

  // 3. Allgemeines blockquote
  const blockquotes = doc.querySelectorAll("blockquote");
  if (blockquotes.length > 0) {
    return blockquotes[0];
  }

  return null;
}

/**
 * Hüllt den zitierten Verlauf / vorherige Antworten in einen speziellen Container
 * mit grauem Overlay ein, sodass die neue Nachricht oben klar abgegrenzt ist.
 */
export function wrapThreadHistory(html, threadLabel = "Vorherige Nachrichten im Verlauf") {
  const source = String(html ?? "").trim();
  if (!source) return source;

  let doc;
  try {
    doc = new DOMParser().parseFromString(source, "text/html");
  } catch {
    return source;
  }

  if (doc.querySelector(".email-thread-history")) {
    return doc.body.innerHTML;
  }

  const boundary = findThreadBoundary(doc);
  if (!boundary) {
    return doc.body.innerHTML;
  }

  let startNode = boundary;
  const parent = startNode.parentElement;
  if (!parent) return doc.body.innerHTML;

  // Wenn direkt davor ein HR liegt, nimm das HR mit in den Verlauf
  const prev = startNode.previousElementSibling;
  if (prev && prev.tagName === "HR") {
    startNode = prev;
  }

  const nodesToWrap = [];
  let curr = startNode;
  while (curr) {
    const next = curr.nextSibling;
    nodesToWrap.push(curr);
    curr = next;
  }

  if (!nodesToWrap.length) return doc.body.innerHTML;

  const wrapper = doc.createElement("div");
  wrapper.className = "email-thread-history";

  if (threadLabel) {
    const badge = doc.createElement("div");
    badge.className = "email-thread-badge";
    badge.textContent = threadLabel;
    wrapper.appendChild(badge);
  }

  parent.insertBefore(wrapper, startNode);

  for (const node of nodesToWrap) {
    wrapper.appendChild(node);
  }

  return doc.body.innerHTML;
}

/**
 * Wandelt reinen Text (z. B. Emails.bodyText bei migrierten Mails) in sicheres HTML mit
 * Absätzen um.
 */
export const plainTextToHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .split("\n")
    .map((line) => `<p>${line || "&nbsp;"}</p>`)
    .join("");

/**
 * Das Dokument für den iframe.
 *
 * Die CSP steht IM Dokument und nicht am iframe, weil `srcdoc` kein eigener Origin mit
 * eigenen Response-Headern ist — ein `<meta http-equiv>` ist hier der Weg, der greift.
 * `default-src 'none'` ist die Grundstellung: kein Skript, kein Frame, keine Schrift,
 * kein Verbindungsaufbau. Erlaubt wird nur, was eine Mail zum Aussehen braucht.
 */
export const buildDocument = (html, allowRemoteImages, threadLabel = "Vorherige Nachrichten im Verlauf") => {
  const imgSrc = allowRemoteImages ? "data: https: http:" : "data:";
  const processedHtml = wrapThreadHistory(html, threadLabel);
  return `<!doctype html>
<html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src ${imgSrc};">
<base target="_blank">
<style>
  html,body{margin:0;padding:20px 24px;font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.55;color:#313a46;background:#fff;}
  img{max-width:100%;height:auto;}
  table{max-width:100%;}
  a{color:#2745c4;}
  p{margin:0 0 10px;}
  hr{border:0;border-top:1px solid #eef2f7;margin:16px 0;}
  blockquote{margin:0 0 0 12px;padding-left:12px;border-left:2px solid #dee2e6;color:#6c757d;}
  .email-thread-history {
    background-color: #f6f8fa;
    border: 1px solid #e1e4e8;
    border-left: 4px solid #adb5bd;
    border-radius: 6px;
    padding: 16px 18px;
    margin-top: 24px;
    margin-bottom: 16px;
    color: #495057;
    opacity: 0.88;
    position: relative;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.02);
    transition: opacity 0.15s ease-in-out, background-color 0.15s ease-in-out;
  }
  .email-thread-history:hover {
    opacity: 1;
    background-color: #f1f3f5;
  }
  .email-thread-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    margin-bottom: 14px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6c757d;
    background: #e9ecef;
    border-radius: 4px;
    border: 1px solid #dee2e6;
    user-select: none;
  }
</style>
</head><body>${processedHtml}</body></html>`;
};

// Zerlegt Adresslisten (Array, kommaseparierter String oder Einzelwert) in saubere Einzeladressen.
export const parseAddressList = (entries) => {
  if (!entries) return [];
  if (Array.isArray(entries)) {
    return entries
      .flatMap((e) => (typeof e === "string" ? e.split(/[,;]/) : [e]))
      .map((e) => String(e || "").trim())
      .filter(Boolean);
  }
  return String(entries)
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter(Boolean);
};

// Absender/Empfaenger als eine Zeile. Die DB fuehrt "Name <adresse>" oder nur die
// Adresse — beides wird hier unveraendert gezeigt, Zerlegen brauchte es nur zum Antworten.
const addressLine = (entries) =>
  (Array.isArray(entries) ? entries : [entries]).filter(Boolean).join(", ");

// Absender, Empfaenger, Cc und Anhaenge stehen NEBEN der Nachricht, nicht nur in der
// Verlaufszeile: das Fenster ist die Stelle, an der eine Mail gelesen wird, und "an wen
// ging das eigentlich" ist dort die haeufigste Frage. Aufgebaut wie die Leseansicht
// eines Mailprogramms — Kopf mit Absender, darunter der Text.
const MetaRow = ({ label, value, truncate, strong }) =>
  value ? (
    <div className="d-flex gap-1 small">
      <span className="text-muted flex-shrink-0">{label}:</span>
      <span
        className={`${strong ? "fw-semibold text-dark" : "text-muted"} ${
          truncate ? "text-truncate" : "text-break"
        }`}
      >
        {value}
      </span>
    </div>
  ) : null;

// "Tobias Horsch <t.horsch@lineup.de>" — die Schreibweise, in der eine Adresse in
// jedem Mailprogramm steht. Fehlt der Name, steht nur die Adresse da.
export const senderLine = (name, email) =>
  name && email && name !== email ? `${name} <${email}>` : name || email || "";

// Initialen fuer das Absenderzeichen. "Max Mustermann" -> MM, "info@kunde.de" -> IN.
export const initials = (name, email) => {
  const source = (name || email || "").replace(/[<>"]/g, " ").trim();
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  const letters = (parts[0]?.[0] || "") + (parts[1]?.[0] || parts[0]?.[1] || "");
  return letters.toUpperCase() || "?";
};

// Nur wenn im Text wirklich Fernbilder stehen, wird die Sperre erwaehnt. Ein Hinweis
// ueber jeder Mail waere Rauschen — die meisten haben keine.
export const hasRemoteImages = (html) =>
  /(?:<img[^>]*\ssrc=["']?https?:|<[^>]*\sbackground=["']?https?:|url\(\s*["']?https?:)/i.test(
    html || ""
  );

/**
 * Zwei Einstiege, weil es zwei Aufrufer gibt:
 *
 * @param email    die Mail-Zeile aus dem Verlauf (rawId, subject, from*, toEmails,
 *                 ccEmails, hasAttachments) — die Kopfdaten liegen dann schon vor.
 * @param emailId  nur die ID (Mail-Aufgabe: ein Eintrag aus `Tasks.activityIDs`). Die Kopfdaten werden
 *                 beim Klick mitgeholt — eine Abfrage per ID, kein Abzug ueber `Emails`.
 */
const EmailFullBody = ({ email, emailId: emailIdProp }) => {
  const { t } = useTranslation(["components", "common"]);
  // Nachgeladene Kopfdaten haben Vorrang vor der uebergebenen Zeile — sie sind aktueller.
  const [loaded, setLoaded] = useState(null);
  const head = loaded || email;
  const emailId = email?.rawId || emailIdProp;
  const subject = head?.subject || head?.title || "";
  const [html, setHtml] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [allowRemoteImages, setAllowRemoteImages] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Vorschau eines Anhangs: das Lesefenster tritt so lange zur Seite und kommt danach
  // zurueck. Es wird dabei nur VERSTECKT, nicht geschlossen — die Vorschau haengt als
  // Kind an der Anhangliste hier drin und wuerde mit dem Fenster verschwinden.
  const [previewing, setPreviewing] = useState(false);
  // `timestamp` kommt aus der Verlaufszeile, `receivedAt` aus der Abfrage per ID.
  const sentAt = head?.timestamp || head?.receivedAt || null;
  const outbound = head?.direction === "outbound";

  const load = async () => {
    if (html !== null) {
      setOpen(true);
      return;
    }
    setBusy(true);
    try {
      let currentHead = head;
      if (!email || !currentHead?.bodyText) {
        try {
          const record = (
            await client.graphql({
              query: getEmailForView,
              variables: { id: emailId },
              skipGlobalError: true,
            })
          ).data?.getEmails;
          if (record) {
            currentHead = {
              ...(email || {}),
              ...record,
              hasAttachments:
                record.hasAttachments === "true" || record.hasAttachments === true,
            };
            setLoaded(currentHead);
          } else if (!email) {
            toast.info(t("This email no longer exists."));
            return;
          }
        } catch (gqlErr) {
          console.warn("getEmailForView fehlgeschlagen", gqlErr);
        }
      }

      // 1. Wenn die Mail eine Graph-Nachricht hat, formatierte Fassung live aus dem Postfach holen.
      // `hasGraphMessage` als Rueckfall: die Verlaufszeile (ActivityFeed.js) reicht nur diese
      // Boolean-Ableitung durch, nie das rohe graphMessageId — und der obige Refetch laeuft
      // NICHT, sobald `bodyText` schon gefuellt ist (praktisch immer). Ohne den Rueckfall bricht
      // das hier fuer JEDE ueber den Verlauf geoeffnete Mail: der Lambda-Call wuerde nie mehr
      // laufen, weil currentHead.graphMessageId in diesem Pfad nie gesetzt wird.
      if (currentHead?.graphMessageId || currentHead?.hasGraphMessage) {
        try {
          const result = await callLambda("lineupdashboardApiCrmMail", {
            body: { action: "fullBody", emailId },
          });
          if (result?.html) {
            setHtml(sanitizeHtml(result.html));
            setAllowRemoteImages(false);
            setShowDetails(false);
            setOpen(true);
            return;
          }
          if (result?.notFoundInMailbox && !currentHead?.bodyText && !currentHead?.bodyPreview) {
            toast.warn(
              t(
                "This email is no longer available in the Outlook mailbox (it may have been deleted).",
                "Diese E-Mail ist im Outlook-Postfach nicht mehr verfügbar (sie wurde eventuell gelöscht)."
              )
            );
            return;
          }
        } catch (lambdaErr) {
          console.warn("Graph fullBody fehlgeschlagen, pruefe bodyText-Fallback", lambdaErr);
        }
      }

      // 2. Fallback: Bei migrierten Mails (ohne graphMessageId) oder wenn Graph kein HTML liefert,
      // nutzen wir den in der Datenbank gespeicherten vollstaendigen bodyText (oder bodyPreview).
      const rawText = currentHead?.bodyText || currentHead?.bodyPreview;
      if (rawText) {
        setHtml(plainTextToHtml(rawText));
        setAllowRemoteImages(false);
        setShowDetails(false);
        setOpen(true);
        return;
      }

      toast.info(t("No formatted version available for this email."));
    } catch (error) {
      console.error("Vollstaendige Fassung fehlgeschlagen", error);
      toast.error(t("Could not load the full email."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="link"
        size="sm"
        className="p-0 small text-muted"
        disabled={busy}
        onClick={load}
      >
        <i className="mdi mdi-text-box-search-outline me-1" />
        {busy ? t("Loading...") : t("Show full message")}
      </Button>

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        size="xl"
        scrollable
        className={previewing ? "d-none" : undefined}
        backdropClassName={previewing ? "d-none" : undefined}
      >
        <Modal.Header closeButton className="align-items-start">
          <div className="pe-2">
            <Modal.Title as="h5" className="mb-1 text-break">
              {subject || t("(No subject)")}
            </Modal.Title>
            <div className="small text-muted d-flex align-items-center flex-wrap gap-2">
              <span>
                <i
                  className={`mdi ${
                    outbound
                      ? "mdi-email-send-outline"
                      : "mdi-email-receive-outline"
                  } me-1`}
                />
                {outbound ? t("Sent") : t("Received")}
              </span>
              {sentAt && (
                <span>
                  {dateFormatter(sentAt)} · {dateTimeFormatter(sentAt)}
                </span>
              )}
              {head?.hasAttachments && (
                <span>
                  <i className="mdi mdi-paperclip me-1" />
                  {t("Attachments")}
                </span>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          {/* Kopf wie in der Leseansicht eines Mailprogramms: Absenderzeichen, daneben
              From/To in der ueblichen Schreibweise "Name <adresse>". Cc und die vollen
              Listen stehen hinter "Details" — sonst schiebt eine Mail an zwanzig Leute
              den Text aus dem Bild. */}
          <div className="d-flex gap-2 p-3 border-bottom">
            <span className="avatar-sm flex-shrink-0">
              <span className="avatar-title bg-primary-lighten text-primary rounded-circle font-13">
                {initials(head?.fromName, head?.fromEmail)}
              </span>
            </span>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              {(() => {
                const toList = parseAddressList(head?.toEmails);
                const ccList = parseAddressList(head?.ccEmails);
                const hasMultipleRecipients = toList.length > 1;
                const hasCc = ccList.length > 0;
                const showDetailsButton = hasMultipleRecipients || hasCc;

                return (
                  <>
                    <div>
                      <MetaRow
                        label={t("From")}
                        value={
                          senderLine(head?.fromName, head?.fromEmail) ||
                          t("Unknown sender")
                        }
                        strong
                      />
                      <MetaRow
                        label={t("To")}
                        value={addressLine(head?.toEmails)}
                        truncate={!showDetails && showDetailsButton}
                      />
                      {showDetails && hasCc && (
                        <MetaRow label={t("CC")} value={addressLine(head?.ccEmails)} />
                      )}
                    </div>
                    {showDetailsButton && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 small text-muted"
                        onClick={() => setShowDetails((prev) => !prev)}
                      >
                        {t("Details")}
                        <i
                          className={`mdi ms-1 ${
                            showDetails ? "mdi-chevron-up" : "mdi-chevron-down"
                          }`}
                        />
                      </Button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          {head?.hasAttachments && (
            <div className="px-3 py-2 border-bottom bg-light-lighten">
              <EmailAttachments emailId={emailId} onPreviewChange={setPreviewing} />
            </div>
          )}
          {/* Der Hinweis steht ueber der Nachricht statt als Schalter im Fuss: dort ist
              er zu sehen, wenn die Luecken im Text auffallen. */}
          {!allowRemoteImages && hasRemoteImages(html) && (
            <div className="d-flex justify-content-between align-items-center gap-2 px-3 py-2 border-bottom bg-warning-lighten small">
              <span>
                <i className="mdi mdi-image-off-outline me-1" />
                {t("Remote images are blocked.")}
              </span>
              <Button
                variant="link"
                size="sm"
                className="p-0 small"
                onClick={() => setAllowRemoteImages(true)}
              >
                {t("Load remote images")}
              </Button>
            </div>
          )}
          {/* sandbox="" ist die strengste Stellung: kein Skript, kein gleicher Origin,
              keine Formulare. ponytail: damit sind auch Links im Mailtext wirkungslos —
              die Grenze ist bewusst so gesetzt, „abgeschottet" heißt abgeschottet.
              Ceiling: wer Links braucht, ergänzt `allow-popups
              allow-popups-to-escape-sandbox`; alles andere bleibt, wie es ist. */}
          <iframe
            title={subject || t("Email")}
            sandbox=""
            srcDoc={buildDocument(
              html || "",
              allowRemoteImages,
              t("Previous messages in thread")
            )}
            style={{ width: "100%", height: "62vh", border: 0, display: "block" }}
          />
        </Modal.Body>
        {/* Kein Fuss: das X oben rechts schliesst dasselbe Fenster. */}
      </Modal>
    </>
  );
};

export default EmailFullBody;
